import { CXRootStore } from '../../stores/cx-root.store';
import dayjs from '~/common/dayjs';
import {
    IScheduleContainerBlockResponse,
    IServiceSelectionItemBlockResponse,
    ISessionConfig
} from '~/types/server.types';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { TFullTimezone } from '~/utils/timezone.utils';
import type { CXScheduleContainerBlock } from '~/components/ClientExperience/OneFlow/models/cx-schedule-container-block.model';
import { TTrackFunction } from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { setSlotSelectedInStorage } from '~/common/Schedule/schedule.utils';
import { ErrorType } from '~/components/ClientExperience/OneFlow/stores/flow-view.ui.store';
import i18next from 'i18next';
import { generateSchedulerData } from '~/components/ClientExperience/OneFlow/controllers/generateSchedulerData';

export type TSchedulerSlotError =
    | 'slot_unavailable'
    | 'unavailable_session'
    | 'session_updated';

export class CXScheduleController {
    rootStore: CXRootStore;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    async getAvailabilities({
        month,
        year,
        timezone,
        shouldClearData,
        sessionConfig
    }: {
        month: number;
        year: number;
        timezone: TFullTimezone;
        shouldClearData?: boolean;
        sessionConfig: ISessionConfig;
    }) {
        const {
            scheduleService,
            flowStore: {
                flow: { workspace_id }
            },
            flowViewUiStore: { isPublicTemplateMode }
        } = this.rootStore;

        const firstDayOfCurrentMonth = dayjs(new Date(year, month, 1)).format();

        const firstDayOfMonthAfterNext = dayjs(firstDayOfCurrentMonth)
            .add(2, 'month')
            .startOf('month')
            .format('YYYY-MM-DD');

        const firstDayOfPreviousMonth = dayjs(firstDayOfCurrentMonth)
            .subtract(1, 'month')
            .startOf('month')
            .format('YYYY-MM-DD');

        // If this is a public template, we want to generate availabilities
        let response;
        if (isPublicTemplateMode) {
            response = generateSchedulerData({
                startDate: firstDayOfPreviousMonth,
                endDate: firstDayOfMonthAfterNext,
                sessionConfig
            });
            if (!response) {
                return undefined;
            }
        } else {
            response = await scheduleService.getAvailabilities({
                startDate: firstDayOfPreviousMonth,
                endDate: firstDayOfMonthAfterNext,
                timezone: timezone,
                sessionTypeId: sessionConfig._id,
                placementId: sessionConfig.placement_id,
                workspaceId: workspace_id,
                source: 'SchedulerBlock'
            });
        }

        const rawAvailSlots = response?.dates;

        this.rootStore.scheduleAvailabilitiesStore.setScheduleSessionSlots(
            rawAvailSlots,
            shouldClearData
        );

        return rawAvailSlots;
    }

    async selectSlot(
        block: CXScheduleContainerBlock,
        selectedDate: string,
        timezone: TFullTimezone,
        track: TTrackFunction
    ) {
        const {
            scheduleService,
            flowStore,
            flowCtrl,
            userStore: { currUser },
            flowViewUiStore: {
                selectedFlow,
                setIsFormSubmitting,
                isRescheduleMode,
                setIsRescheduleMode
            }
        } = this.rootStore;

        await this.approvePendingChanges();

        const {
            sessionConfig,
            flow_id,
            page_id,
            flow: { revision_id },
            connected_variable_id,
            isRequireToPay,
            ownerId
        } = block;

        if (isRequireToPay) {
            block.setTempVariableValue({
                date_time_start: selectedDate,
                client_timezone: timezone,
                client: {
                    _id: currUser._id,
                    full_name: currUser.full_name,
                    email: currUser.email
                },
                session_name: sessionConfig.session_name,
                session_type: sessionConfig.session_type,
                session_duration: sessionConfig.session_duration,
                meeting_location: sessionConfig.meeting_location,
                session_description: sessionConfig.session_description,
                session_config_id: sessionConfig._id
            });
            setSlotSelectedInStorage(flow_id);
            return;
        }

        try {
            setIsFormSubmitting(true);

            const { memberIds, error } = await this.validateSlot(
                block,
                selectedDate,
                timezone
            );

            if (error) {
                track(AnalyticsEvents.SlotNotAvailable, {
                    action: 'load',
                    source: 'scheduler page',
                    type: error
                });
                this.renderErrorToast(error);
                return;
            }

            const selectSlotResponse = await scheduleService.selectSlot({
                flowId: flow_id,
                revisionId: revision_id,
                pageId: page_id,
                variableId: connected_variable_id,
                selectedDate,
                timezone,
                userIds: memberIds
            });
            selectedFlow.setFlowStatus(selectSlotResponse.new_status);
            flowStore.setVariableValue(
                selectSlotResponse.variable.variable_id,
                selectSlotResponse.variable.value
            );

            let status = 'schedule';
            let is_different_member = false;

            if (isRescheduleMode) {
                status = 'reschedule';
                is_different_member = !memberIds.includes(ownerId);
                setIsRescheduleMode(false);
            }

            track(AnalyticsEvents.MeetingWasScheduled, {
                source: 'immediately',
                status,
                is_different_member
            });
        } catch (error) {
            this.handleSchedulerErrors(error);
            flowCtrl.genericErrorHandlingCX(error, selectedFlow);
        } finally {
            setIsFormSubmitting(false);
        }
    }

    async validateSlot(
        block: CXScheduleContainerBlock,
        selectedDate: string,
        timezone: TFullTimezone
    ): Promise<{ memberIds?: string[]; error?: TSchedulerSlotError }> {
        const {
            scheduleService,
            flowCtrl,
            flowStore: {
                flow: { workspace_id, sessionSelectionBlock }
            }
        } = this.rootStore;

        const { sessionConfig, block_id, flow_id } = block;

        const [slotAvailableMembersResponse, fetchFlowResponse] =
            await Promise.allSettled([
                scheduleService.getSlotAvailableMembers({
                    selectedDate,
                    timezone,
                    sessionTypeId: sessionConfig._id,
                    placementId: sessionConfig.placement_id,
                    workspaceId: workspace_id
                }),
                flowCtrl.fetchFlow(flow_id, false)
            ]);

        if (fetchFlowResponse.status === 'rejected') {
            throw fetchFlowResponse.reason;
        }

        const { flow, pages, blocks, variables } = fetchFlowResponse.value;

        const updateFlow = () =>
            flowCtrl.updateFlow(flow, pages, blocks, variables);

        if (slotAvailableMembersResponse.status === 'rejected') {
            updateFlow();
            return { error: ErrorType.sessionUnavailable };
        }

        const fetchedBlock = blocks[
            block_id
        ] as IScheduleContainerBlockResponse;

        if (
            !sessionSelectionBlock &&
            (!fetchedBlock.session_config?.enabled ||
                !fetchedBlock.session_config)
        ) {
            updateFlow();
            return { error: ErrorType.sessionUnavailable };
        }

        let originalSessionConfigHash: string;
        let fetchedSessionConfigHash: string;

        if (!!sessionSelectionBlock) {
            const fetchedItemBlock = blocks[
                sessionSelectionBlock.firstSelectedService.block_id
            ] as IServiceSelectionItemBlockResponse;

            originalSessionConfigHash =
                sessionSelectionBlock.firstSelectedService.session_config_hash;

            fetchedSessionConfigHash = fetchedItemBlock.session_config_hash;
        } else {
            originalSessionConfigHash = block.session_config_hash;
            fetchedSessionConfigHash = fetchedBlock.session_config_hash;
        }

        if (originalSessionConfigHash !== fetchedSessionConfigHash) {
            updateFlow();
            return { error: ErrorType.sessionUpdated };
        }

        const { memberIds } = slotAvailableMembersResponse.value;

        if (memberIds.length === 0) {
            return { error: ErrorType.slotUnavailable };
        }

        return { memberIds };
    }

    renderErrorToast(error: TSchedulerSlotError) {
        this.rootStore.toastStore.add({
            type: ToastType.Error,
            text: i18next.t(`client-experience:schedule_block.errors.${error}`),
            autoDismissMs: 12000
        });
    }

    handleSchedulerErrors(error) {
        if (error?.data?.error_type === 'HBUserError') {
            this.rootStore.toastStore.add({
                type: ToastType.Error,
                text: error.data.error_message,
                autoDismissMs: 12000
            });
        }
    }

    async cancelSession(block: CXScheduleContainerBlock, message: string) {
        const {
            scheduleService,
            flowStore,
            flowCtrl,
            flowViewUiStore: { selectedFlow, setIsFormSubmitting }
        } = this.rootStore;

        const {
            flow_id,
            page_id,
            flow: { revision_id },
            connected_variable_id
        } = block;

        setIsFormSubmitting(true);

        try {
            const selectSlotResponse = await scheduleService.cancelSession({
                flowId: flow_id,
                revisionId: revision_id,
                pageId: page_id,
                variableId: connected_variable_id,
                message
            });
            selectedFlow.setFlowStatus(selectSlotResponse.new_status);
            flowStore.setVariableValue(
                selectSlotResponse.variable.variable_id,
                selectSlotResponse.variable.value
            );
        } catch (error) {
            this.handleSchedulerErrors(error);
            flowCtrl.genericErrorHandlingCX(error, selectedFlow);
        } finally {
            setIsFormSubmitting(false);
        }
    }

    async approvePendingChanges() {
        const {
            flowCtrl,
            analyticsService,
            flowViewUiStore: {
                blocksToApproveOnPage,
                setShouldShowApproveSuccessProtip,
                selectedFlow
            },
            clientPortalConfigStore: { isPreview }
        } = this.rootStore;
        const schedulerBlockToApprove = blocksToApproveOnPage.find(
            x => x.isScheduleContainerBlock
        );
        if (schedulerBlockToApprove && !isPreview) {
            analyticsService.track(AnalyticsEvents.ClientVersioning, {
                type: 'click: select slot'
            });
            await flowCtrl.approvePendingChange(
                selectedFlow._id,
                schedulerBlockToApprove.block_id
            );
            setShouldShowApproveSuccessProtip(true);
        }
    }
}
